import {
  startOfMonth,
  endOfMonth,
  format,
  addMonths,
  subMonths,
} from "date-fns";
import {
  createBangkokDate,
  getBangkokDateComponents,
  toBangkokTimestamp,
  toBangkokTimestampEndOfDay,
} from "@x-veloper/dataslot-utils";
import type { CommissionPeriod } from "../types";
import { COMMISSION_CONFIG } from "../constants";

/**
 * Calculate commission period dates based on 26th cutoff from year and month
 */
export function getCommissionPeriodFromYearMonth(year: number, month: number): CommissionPeriod {
  // Create a Date in the middle of the specified month to avoid timezone issues
  const referenceDate = new Date(year, month - 1, 15);
  return getCommissionPeriod(referenceDate);
}

/**
 * Calculate commission period dates based on 26th cutoff
 */
export function getCommissionPeriod(date: Date = new Date()): CommissionPeriod {
  // Convert to Bangkok timezone to check the cutoff
  const bangkokComponents = getBangkokDateComponents(date);

  let periodStartYear: number;
  let periodStartMonth: number;
  let periodEndYear: number;
  let periodEndMonth: number;

  if (bangkokComponents.day < COMMISSION_CONFIG.CUTOFF_DAY) {
    // Before 26th, period is from 26th of previous month to 25th of current month
    if (bangkokComponents.month === 1) {
      // January - go to previous year December
      periodStartYear = bangkokComponents.year - 1;
      periodStartMonth = 12;
      periodEndYear = bangkokComponents.year;
      periodEndMonth = 1;
    } else {
      periodStartYear = bangkokComponents.year;
      periodStartMonth = bangkokComponents.month - 1;
      periodEndYear = bangkokComponents.year;
      periodEndMonth = bangkokComponents.month;
    }
  } else {
    // After 26th, period is from 26th of current month to 25th of next month
    if (bangkokComponents.month === 12) {
      // December - go to next year January
      periodStartYear = bangkokComponents.year;
      periodStartMonth = 12;
      periodEndYear = bangkokComponents.year + 1;
      periodEndMonth = 1;
    } else {
      periodStartYear = bangkokComponents.year;
      periodStartMonth = bangkokComponents.month;
      periodEndYear = bangkokComponents.year;
      periodEndMonth = bangkokComponents.month + 1;
    }
  }

  // Create period dates in Bangkok timezone
  const periodStart = createBangkokDate(periodStartYear, periodStartMonth, COMMISSION_CONFIG.CUTOFF_DAY, 0, 0, 0, 0);
  const periodEnd = createBangkokDate(periodEndYear, periodEndMonth, COMMISSION_CONFIG.CUTOFF_DAY - 1, 23, 59, 59, 999);

  return {
    startDate: periodStart,
    endDate: periodEnd,
    year: periodEndYear,
    month: periodEndMonth,
  };
}

/**
 * Get full month date range (1st to last day of month)
 */
export function getFullMonthRange(date: Date = new Date()): CommissionPeriod {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);

  return {
    startDate: monthStart,
    endDate: monthEnd,
    year: monthStart.getFullYear(),
    month: monthStart.getMonth() + 1,
  };
}

/**
 * Get date range from start and end dates
 */
export function getCustomDateRange(
  startDate: Date,
  endDate: Date,
): CommissionPeriod {
  return {
    startDate,
    endDate,
    year: startDate.getFullYear(),
    month: startDate.getMonth() + 1,
  };
}

/**
 * Get previous commission period
 */
export function getPreviousCommissionPeriod(
  currentPeriod: CommissionPeriod,
): CommissionPeriod {
  const previousMonth = subMonths(currentPeriod.startDate, 1);
  return getCommissionPeriod(previousMonth);
}

/**
 * Get next commission period
 */
export function getNextCommissionPeriod(
  currentPeriod: CommissionPeriod,
): CommissionPeriod {
  const nextMonth = addMonths(currentPeriod.startDate, 1);
  return getCommissionPeriod(nextMonth);
}

/**
 * Check if a timestamp falls within a commission period
 * Handles Bangkok timezone properly for accurate comparisons
 */
export function isInCommissionPeriod(
  timestamp: number | string | null | undefined,
  period: CommissionPeriod,
): boolean {
  if (!timestamp) return false;

  // Convert string timestamps to number if needed
  const paymentTimestamp = typeof timestamp === 'string'
    ? new Date(timestamp).getTime()
    : timestamp;

  // Convert period boundaries to Bangkok timezone timestamps for accurate comparison
  const periodStartBangkok = toBangkokTimestamp(period.startDate);
  const periodEndBangkok = toBangkokTimestampEndOfDay(period.endDate);

  // Direct timestamp comparison (all in Bangkok timezone context)
  const isInPeriod = paymentTimestamp >= periodStartBangkok && paymentTimestamp <= periodEndBangkok;
  return isInPeriod;
}

/**
 * Format commission period for display
 */
export function formatCommissionPeriod(period: CommissionPeriod): string {
  const startMonth = format(period.startDate, "MMM");
  const endMonth = format(period.endDate, "MMM");
  const startYear = format(period.startDate, "yyyy");
  const endYear = format(period.endDate, "yyyy");

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startMonth} ${startYear}`;
    } else {
      return `${startMonth} - ${endMonth} ${startYear}`;
    }
  } else {
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
  }
}

// Re-export all general date utilities from dataslot-utils for convenience
export {
  createBangkokDate,
  nowInBangkok,
  toBangkokDate,
  formatBangkokDate,
  getBangkokDateComponents,
  toBangkokTimestamp,
  toBangkokTimestampEndOfDay,
  toBangkokISOString,
  formatCurrency,
  formatPercentage,
} from "@x-veloper/dataslot-utils";